import { useEffect, useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { useLang, track } from "@/context/LanguageContext";
import { Reveal, ImageReveal } from "@/components/Reveal";
import { SectionLabel } from "@/components/Editorial";
import { usePageMeta } from "@/lib/seo";
import { API } from "@/lib/api";
import { IMAGES } from "@/data/config";

const INTEREST_KEYS = ["team_session", "wellness", "event", "tailored", "not_sure"];
const FREQ_KEYS = ["one_off", "recurring", "not_sure"];

const COPY = {
  en: {
    eyebrow: "Maison Core for Business",
    heroTitle: "Move together.",
    heroLead: "Private Reformer Pilates experiences for teams, companies and organisations in Amsterdam.",
    heroSub: "From one-off team sessions to recurring corporate wellness programmes — designed around your people.",
    heroCta: "Request a corporate session",
    heroCta2: "Explore the options",
    introLabel: "A private studio, reserved for your team",
    introTitle: "A different way to bring your team together.",
    introBody: "Maison Core opens its studio exclusively for your organisation — a private Reformer Pilates experience built around movement, strength, focus and connection. Instructor-led, thoughtfully paced and welcoming to every level of experience.",
    expLabel: "The experiences",
    experiences: [
      { key: "team_session", name: "Private team session", body: "A private Reformer Pilates class exclusively for your team." },
      { key: "wellness", name: "Corporate wellness", body: "Recurring sessions that make movement part of your organisation's wellbeing programme." },
      { key: "event", name: "Team & company events", body: "A premium movement experience for team days, company events or employee wellbeing initiatives." },
      { key: "tailored", name: "Tailored experience", body: "Have something different in mind? We create an experience around your organisation's needs." },
    ],
    enquire: "Enquire",
    tailoredNote: "Tailored to your team · Available on request",
    whyLabel: "Why Maison Core",
    why: [
      { t: "Private studio experience", b: "A boutique setting reserved exclusively for your team." },
      { t: "Reformer Pilates", b: "Instructor-led movement focused on strength, control and mobility." },
      { t: "Tailored to your team", b: "Sessions adapted to different experience levels — beginners welcome." },
      { t: "Amsterdam location", b: "Conveniently located in Buitenveldert, close to the Zuidas and surrounding organisations." },
    ],
    localLabel: "The location",
    localTitle: "Close to work. A world away from the office.",
    localBody: "Maison Core is located in Amsterdam Buitenveldert, within easy reach of the Zuidas and surrounding business districts, schools and healthcare organisations.",
    localList: ["Before-work sessions", "Team wellness moments", "Company wellbeing days", "Private team events"],
    howLabel: "How it works",
    steps: [
      { t: "Tell us about your team", b: "Share your preferred date, group size and what you have in mind." },
      { t: "We create the experience", b: "Maison Core proposes an appropriate session or format for your organisation." },
      { t: "Move together", b: "Your team joins us at Maison Core for their private Pilates experience." },
    ],
    formLabel: "Corporate enquiry",
    formTitle: "Let's create something for your team.",
    formLead: "Tell us a little about your organisation and what you have in mind. We'll get back to you to discuss the possibilities.",
    fFirst: "First name",
    fLast: "Last name",
    fCompany: "Company / organisation",
    fEmail: "Work email",
    fPhone: "Phone number",
    fOrgType: "Type of organisation",
    orgTypes: ["Company / office", "School / education", "Healthcare", "Non-profit", "Other"],
    fParticipants: "Number of participants",
    fInterest: "What are you interested in?",
    interests: { team_session: "Private team Reformer Pilates", wellness: "Corporate wellness / recurring sessions", event: "Team or company event", tailored: "Tailored experience", not_sure: "Not sure yet" },
    fDate: "Preferred date",
    fTime: "Preferred time / time of day",
    fFrequency: "How often?",
    freqs: { one_off: "One-off", recurring: "Recurring", not_sure: "Not sure yet" },
    fMessage: "Tell us what you have in mind",
    consent: "I agree that Maison Core may use my details to respond to this enquiry.",
    optional: "Optional",
    submit: "Request a proposal",
    sending: "Sending…",
    vRequired: "Please complete all required fields.",
    vEmail: "Please enter a valid work email address.",
    vInterest: "Please select what you are interested in.",
    vConsent: "Please accept the privacy consent.",
    error: "Something went wrong. Please try again.",
    successTitle: "Thank you.",
    successBody: "We've received your request. The Maison Core team will be in touch to discuss the possibilities.",
    finalTitle: "Bring your team to the Reformer.",
    finalBody: "From a single private session to an ongoing wellness programme, we'll create an experience around your team.",
    finalCta: "Start your request",
  },
  nl: {
    eyebrow: "Maison Core for Business",
    heroTitle: "Beweeg samen.",
    heroLead: "Privé Reformer Pilates ervaringen voor teams, bedrijven en organisaties in Amsterdam.",
    heroSub: "Van eenmalige teamsessies tot terugkerende corporate wellness programma's — ontworpen rond jouw mensen.",
    heroCta: "Vraag een corporate sessie aan",
    heroCta2: "Bekijk de mogelijkheden",
    introLabel: "Een privéstudio, gereserveerd voor jouw team",
    introTitle: "Een andere manier om je team samen te brengen.",
    introBody: "Maison Core opent de studio exclusief voor jouw organisatie — een privé Reformer Pilates ervaring rond beweging, kracht, focus en verbinding. Onder begeleiding van een instructor, in een doordacht tempo en toegankelijk voor elk niveau.",
    expLabel: "De ervaringen",
    experiences: [
      { key: "team_session", name: "Privé teamsessie", body: "Een privé Reformer Pilates les, exclusief voor jouw team." },
      { key: "wellness", name: "Corporate wellness", body: "Terugkerende sessies die beweging onderdeel maken van het wellbeing-programma van je organisatie." },
      { key: "event", name: "Team- & bedrijfsevents", body: "Een premium beweegervaring voor teamdagen, bedrijfsevents of employee wellbeing initiatieven." },
      { key: "tailored", name: "Op maat", body: "Iets anders in gedachten? Wij creëren een ervaring rond de wensen van jouw organisatie." },
    ],
    enquire: "Informeer",
    tailoredNote: "Op maat voor jouw team · Op aanvraag beschikbaar",
    whyLabel: "Waarom Maison Core",
    why: [
      { t: "Privé studio-ervaring", b: "Een boutique setting, exclusief gereserveerd voor jouw team." },
      { t: "Reformer Pilates", b: "Begeleide beweging gericht op kracht, controle en mobiliteit." },
      { t: "Op maat voor jouw team", b: "Sessies aangepast aan verschillende niveaus — beginners welkom." },
      { t: "Amsterdamse locatie", b: "Gunstig gelegen in Buitenveldert, dicht bij de Zuidas en omliggende organisaties." },
    ],
    localLabel: "De locatie",
    localTitle: "Dicht bij het werk. Een wereld weg van kantoor.",
    localBody: "Maison Core ligt in Amsterdam Buitenveldert, op korte afstand van de Zuidas en omliggende zakendistricten, scholen en zorgorganisaties.",
    localList: ["Sessies vóór werktijd", "Team wellness momenten", "Company wellbeing dagen", "Privé teamevents"],
    howLabel: "Hoe het werkt",
    steps: [
      { t: "Vertel ons over je team", b: "Deel je gewenste datum, groepsgrootte en wat je in gedachten hebt." },
      { t: "Wij creëren de ervaring", b: "Maison Core stelt een passende sessie of vorm voor jouw organisatie voor." },
      { t: "Beweeg samen", b: "Je team komt naar Maison Core voor hun privé Pilates ervaring." },
    ],
    formLabel: "Corporate aanvraag",
    formTitle: "Laten we iets voor jouw team creëren.",
    formLead: "Vertel ons kort iets over je organisatie en wat je in gedachten hebt. We nemen contact op om de mogelijkheden te bespreken.",
    fFirst: "Voornaam",
    fLast: "Achternaam",
    fCompany: "Bedrijf / organisatie",
    fEmail: "Zakelijk e-mailadres",
    fPhone: "Telefoonnummer",
    fOrgType: "Type organisatie",
    orgTypes: ["Bedrijf / kantoor", "School / onderwijs", "Zorg", "Non-profit", "Anders"],
    fParticipants: "Aantal deelnemers",
    fInterest: "Waar ben je in geïnteresseerd?",
    interests: { team_session: "Privé team Reformer Pilates", wellness: "Corporate wellness / terugkerende sessies", event: "Team- of bedrijfsevent", tailored: "Ervaring op maat", not_sure: "Nog niet zeker" },
    fDate: "Gewenste datum",
    fTime: "Gewenste tijd / dagdeel",
    fFrequency: "Hoe vaak?",
    freqs: { one_off: "Eenmalig", recurring: "Terugkerend", not_sure: "Nog niet zeker" },
    fMessage: "Vertel ons wat je in gedachten hebt",
    consent: "Ik ga ermee akkoord dat Maison Core mijn gegevens gebruikt om op deze aanvraag te reageren.",
    optional: "Optioneel",
    submit: "Vraag een voorstel aan",
    sending: "Versturen…",
    vRequired: "Vul alle verplichte velden in.",
    vEmail: "Vul een geldig zakelijk e-mailadres in.",
    vInterest: "Selecteer waar je in geïnteresseerd bent.",
    vConsent: "Accepteer de privacyverklaring.",
    error: "Er ging iets mis. Probeer het opnieuw.",
    successTitle: "Dank je wel.",
    successBody: "We hebben je aanvraag ontvangen. Het Maison Core team neemt contact op om de mogelijkheden te bespreken.",
    finalTitle: "Breng je team naar de Reformer.",
    finalBody: "Van een enkele privésessie tot een doorlopend wellness programma — wij creëren een ervaring rond jouw team.",
    finalCta: "Start je aanvraag",
  },
};

const field = "w-full border-b border-onyx/25 bg-transparent py-4 font-sans text-lg text-onyx placeholder:text-onyx/40 focus:border-onyx focus:outline-none transition-colors";
const labelCls = "label text-terracotta";

const scrollToForm = () => document.getElementById("corporate-enquiry")?.scrollIntoView({ behavior: "smooth" });

const initialForm = {
  first_name: "", last_name: "", company: "", email: "", phone: "",
  organisation_type: "", participants: "", interest: "", preferred_date: "",
  preferred_time: "", frequency: "", message: "", consent: false, website: "",
};

export default function Corporate() {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  const [f, setF] = useState(initialForm);
  const [status, setStatus] = useState("idle");

  usePageMeta(
    lang === "nl"
      ? {
          title: "Corporate Reformer Pilates Amsterdam | Maison Core",
          description: "Privé Reformer Pilates voor teams en organisaties in Amsterdam Buitenveldert, nabij de Zuidas. Teamsessies, corporate wellness en bedrijfsevents — op maat en op aanvraag.",
          canonical: "https://maisoncore.nl/corporate",
        }
      : {
          title: "Corporate Reformer Pilates Amsterdam | Maison Core",
          description: "Private Reformer Pilates for teams and organisations in Amsterdam Buitenveldert, near the Zuidas. Team sessions, corporate wellness and company events — tailored, available on request.",
          canonical: "https://maisoncore.nl/corporate",
        }
  );

  useEffect(() => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      name: "Corporate Reformer Pilates",
      serviceType: "Corporate wellness / team Pilates sessions",
      areaServed: "Amsterdam",
      provider: {
        "@type": "LocalBusiness",
        name: "Maison Core",
        address: { "@type": "PostalAddress", streetAddress: "Buitenveldertselaan 120", postalCode: "1081 AB", addressLocality: "Amsterdam", addressCountry: "NL" },
      },
    });
    document.head.appendChild(s);
    return () => { document.head.removeChild(s); };
  }, []);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const preselect = (key) => {
    setF((p) => ({ ...p, interest: key }));
    track("corporate_enquire_click", { interest: key });
    scrollToForm();
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.first_name.trim() || !f.last_name.trim() || !f.company.trim() || !f.email.trim() || !f.participants.trim()) { toast.error(c.vRequired); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) { toast.error(c.vEmail); return; }
    if (!f.interest) { toast.error(c.vInterest); return; }
    if (!f.consent) { toast.error(c.vConsent); return; }
    if (status === "sending") return;
    setStatus("sending");
    try {
      await axios.post(`${API}/corporate-enquiry`, {
        first_name: f.first_name.trim(),
        last_name: f.last_name.trim(),
        company: f.company.trim(),
        email: f.email.trim(),
        phone: f.phone.trim() || null,
        organisation_type: f.organisation_type || null,
        participants: f.participants.trim(),
        interest: f.interest,
        preferred_date: f.preferred_date || null,
        preferred_time: f.preferred_time.trim() || null,
        frequency: f.frequency || null,
        message: f.message.trim() || null,
        consent: f.consent,
        language: lang,
        website: f.website,
      });
      track("corporate_enquiry_submit", { interest: f.interest });
      // GA4 lead event — fires ONLY after confirmed successful submission, no PII
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: "corporate_lead", lead_type: "corporate", page_path: "/corporate", page_location: window.location.href });
      if (typeof window.gtag === "function") {
        window.gtag("event", "corporate_lead", { lead_type: "corporate", page_path: "/corporate" });
      }
      // Google Ads conversion — only after confirmed successful submission
      if (typeof window.gtag === "function") {
        window.gtag("event", "conversion", { send_to: "AW-18304818787/VQKBCOzl6-qcEOO8tZhE" });
      }
      setStatus("success");
    } catch {
      setStatus("idle");
      toast.error(c.error);
    }
  };

  return (
    <>
      <Toaster position="top-center" richColors />

      {/* 1 — HERO */}
      <section className="bg-onyx text-petal" data-testid="corporate-hero">
        <div className="mx-auto grid max-w-[1600px] items-center gap-0 md:grid-cols-2">
          <div className="px-6 pb-16 pt-32 md:px-16 md:pb-24 md:pt-44">
            <SectionLabel light>{c.eyebrow}</SectionLabel>
            <Reveal>
              <h1 className="mt-8 font-serif text-5xl leading-[0.98] sm:text-6xl lg:text-7xl">{c.heroTitle}</h1>
            </Reveal>
            <p className="mt-8 max-w-md font-sans text-lg leading-relaxed text-petal/80">{c.heroLead}</p>
            <p className="mt-4 max-w-md font-sans text-base leading-relaxed text-petal/55">{c.heroSub}</p>
            <div className="mt-12 flex flex-wrap items-center gap-6">
              <button onClick={scrollToForm} className="label border border-petal bg-petal px-8 py-5 text-onyx transition-colors duration-500 hover:bg-transparent hover:text-petal" data-testid="corporate-hero-cta">
                {c.heroCta}
              </button>
              <button onClick={() => document.getElementById("corporate-experiences")?.scrollIntoView({ behavior: "smooth" })} className="label border-b border-petal/60 pb-1 text-petal/80 transition-opacity hover:opacity-60" data-testid="corporate-hero-cta2">
                {c.heroCta2} ↓
              </button>
            </div>
          </div>
          <ImageReveal src={IMAGES.equipRow} alt="Reformer Pilates studio Maison Core Amsterdam" className="aspect-[4/5] md:aspect-auto md:h-full md:min-h-[560px]" />
        </div>
      </section>

      {/* 2 — INTRODUCTION */}
      <section className="bg-petal" data-testid="corporate-intro">
        <div className="mx-auto grid max-w-[1600px] gap-10 px-6 py-20 md:grid-cols-12 md:px-12 md:py-28">
          <div className="md:col-span-4"><SectionLabel>{c.introLabel}</SectionLabel></div>
          <div className="md:col-span-7 md:col-start-6">
            <Reveal><h2 className="max-w-2xl font-serif text-4xl leading-tight md:text-5xl">{c.introTitle}</h2></Reveal>
            <p className="mt-8 max-w-xl font-sans text-lg leading-relaxed text-onyx/70">{c.introBody}</p>
          </div>
        </div>
      </section>

      {/* 3 — EXPERIENCES */}
      <section id="corporate-experiences" className="scroll-mt-24 bg-blush/40" data-testid="corporate-experiences">
        <div className="mx-auto max-w-[1600px] px-6 py-20 md:px-12 md:py-28">
          <SectionLabel>{c.expLabel}</SectionLabel>
          <div className="mt-14 grid gap-6 md:grid-cols-2">
            {c.experiences.map((exp, i) => (
              <Reveal key={exp.key} delay={i * 0.06}>
                <div className="flex h-full flex-col justify-between gap-8 border border-onyx/15 bg-petal p-8 md:p-10" data-testid={`corporate-exp-${exp.key}`}>
                  <div>
                    <p className="label text-terracotta">{String(i + 1).padStart(2, "0")}</p>
                    <h3 className="mt-5 font-serif text-3xl leading-tight">{exp.name}</h3>
                    <p className="mt-4 font-sans text-base leading-relaxed text-onyx/65">{exp.body}</p>
                  </div>
                  <button onClick={() => preselect(exp.key)} className="label w-fit border-b border-onyx pb-1 transition-opacity hover:opacity-60" data-testid={`corporate-enquire-${exp.key}`}>
                    {c.enquire} →
                  </button>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="label mt-10 text-onyx/45">{c.tailoredNote}</p>
        </div>
      </section>

      {/* 4 — WHY MAISON CORE */}
      <section className="bg-petal" data-testid="corporate-why">
        <div className="mx-auto max-w-[1600px] px-6 py-20 md:px-12 md:py-28">
          <SectionLabel>{c.whyLabel}</SectionLabel>
          <div className="mt-14 grid gap-x-10 gap-y-12 sm:grid-cols-2 lg:grid-cols-4">
            {c.why.map((w, i) => (
              <Reveal key={w.t} delay={i * 0.06}>
                <div className="border-t border-onyx/20 pt-6">
                  <h3 className="font-serif text-2xl leading-snug">{w.t}</h3>
                  <p className="mt-3 font-sans text-sm leading-relaxed text-onyx/60">{w.b}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 5 — LOCAL / ZUIDAS */}
      <section className="bg-onyx text-petal" data-testid="corporate-local">
        <div className="mx-auto grid max-w-[1600px] items-center gap-12 px-6 py-20 md:grid-cols-2 md:px-12 md:py-28">
          <ImageReveal src={IMAGES.arch2} alt="Maison Core studio in Amsterdam Buitenveldert" className="aspect-[4/3]" />
          <div>
            <SectionLabel light>{c.localLabel}</SectionLabel>
            <Reveal><h2 className="mt-8 max-w-xl font-serif text-4xl leading-tight md:text-5xl">{c.localTitle}</h2></Reveal>
            <p className="mt-6 max-w-md font-sans text-lg leading-relaxed text-petal/70">{c.localBody}</p>
            <ul className="mt-10 max-w-md">
              {c.localList.map((item, i) => (
                <li key={item} className="flex items-baseline gap-4 border-t border-petal/15 py-4 font-sans text-base text-petal/80">
                  <span className="label text-blush">{String(i + 1).padStart(2, "0")}</span>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* 6 — HOW IT WORKS */}
      <section className="bg-petal" data-testid="corporate-how">
        <div className="mx-auto max-w-[1600px] px-6 py-20 md:px-12 md:py-28">
          <SectionLabel>{c.howLabel}</SectionLabel>
          <div className="mt-14 grid gap-12 md:grid-cols-3 md:gap-10">
            {c.steps.map((s, i) => (
              <Reveal key={s.t} delay={i * 0.08}>
                <div className="border-t border-onyx/20 pt-8">
                  <p className="font-serif text-4xl text-terracotta">{String(i + 1).padStart(2, "0")}</p>
                  <h3 className="mt-6 font-serif text-3xl leading-snug">{s.t}</h3>
                  <p className="mt-4 max-w-sm font-sans text-base leading-relaxed text-onyx/65">{s.b}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* 7 — ENQUIRY FORM */}
      <section id="corporate-enquiry" className="scroll-mt-24 bg-blush/40" data-testid="corporate-form-section">
        <div className="mx-auto max-w-3xl px-6 py-20 md:px-12 md:py-28">
          {status === "success" ? (
            <div data-testid="corporate-success">
              <SectionLabel>{c.formLabel}</SectionLabel>
              <h2 className="mt-8 font-serif text-5xl md:text-6xl">{c.successTitle}</h2>
              <p className="mt-6 max-w-xl font-sans text-lg leading-relaxed text-onyx/70">{c.successBody}</p>
            </div>
          ) : (
            <>
              <SectionLabel>{c.formLabel}</SectionLabel>
              <Reveal><h2 className="mt-8 font-serif text-4xl leading-tight md:text-5xl">{c.formTitle}</h2></Reveal>
              <p className="mt-5 max-w-xl font-sans text-base leading-relaxed text-onyx/65">{c.formLead}</p>
              <form onSubmit={submit} className="mt-14 space-y-10" data-testid="corporate-form">
                <input type="text" value={f.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                <div className="grid gap-10 md:grid-cols-2">
                  <div>
                    <label className={labelCls}>{c.fFirst} *</label>
                    <input value={f.first_name} onChange={set("first_name")} autoComplete="given-name" className={field} data-testid="corp-first-name" />
                  </div>
                  <div>
                    <label className={labelCls}>{c.fLast} *</label>
                    <input value={f.last_name} onChange={set("last_name")} autoComplete="family-name" className={field} data-testid="corp-last-name" />
                  </div>
                  <div>
                    <label className={labelCls}>{c.fCompany} *</label>
                    <input value={f.company} onChange={set("company")} autoComplete="organization" className={field} data-testid="corp-company" />
                  </div>
                  <div>
                    <label className={labelCls}>{c.fEmail} *</label>
                    <input type="email" inputMode="email" value={f.email} onChange={set("email")} autoComplete="email" className={field} data-testid="corp-email" />
                  </div>
                  <div>
                    <label className={labelCls}>{c.fPhone}</label>
                    <input type="tel" inputMode="tel" value={f.phone} onChange={set("phone")} autoComplete="tel" className={field} data-testid="corp-phone" />
                  </div>
                  <div>
                    <label className={labelCls}>{c.fOrgType}</label>
                    <select value={f.organisation_type} onChange={set("organisation_type")} className={`${field} cursor-pointer`} data-testid="corp-org-type">
                      <option value="">—</option>
                      {c.orgTypes.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className={labelCls}>{c.fParticipants} *</label>
                    <input inputMode="numeric" value={f.participants} onChange={set("participants")} className={field} data-testid="corp-participants" />
                  </div>
                </div>

                <div>
                  <p className="font-sans text-base text-onyx/80">{c.fInterest} *</p>
                  <div className="mt-5 flex flex-wrap gap-3">
                    {INTEREST_KEYS.map((k) => (
                      <button
                        type="button"
                        key={k}
                        onClick={() => setF((p) => ({ ...p, interest: k }))}
                        className={`label border px-5 py-3 transition-colors duration-300 ${f.interest === k ? "border-onyx bg-onyx text-petal" : "border-onyx/25 text-onyx/60 hover:border-onyx hover:text-onyx"}`}
                        data-testid={`corp-interest-${k}`}
                        aria-pressed={f.interest === k}
                      >
                        {c.interests[k]}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid gap-10 md:grid-cols-2">
                  <div>
                    <label className={labelCls}>{c.fDate} <span className="text-onyx/40">({c.optional})</span></label>
                    <input type="date" value={f.preferred_date} onChange={set("preferred_date")} className={field} data-testid="corp-date" />
                  </div>
                  <div>
                    <label className={labelCls}>{c.fTime} <span className="text-onyx/40">({c.optional})</span></label>
                    <input value={f.preferred_time} onChange={set("preferred_time")} className={field} data-testid="corp-time" />
                  </div>
                </div>

                <div>
                  <p className="font-sans text-base text-onyx/80">{c.fFrequency}</p>
                  <div className="mt-4 inline-flex flex-wrap border border-onyx/25 p-1">
                    {FREQ_KEYS.map((k) => (
                      <button
                        type="button"
                        key={k}
                        onClick={() => setF((p) => ({ ...p, frequency: k }))}
                        className={`label px-6 py-3 transition-colors duration-300 ${f.frequency === k ? "bg-onyx text-petal" : "text-onyx/55 hover:text-onyx"}`}
                        data-testid={`corp-frequency-${k}`}
                        aria-pressed={f.frequency === k}
                      >
                        {c.freqs[k]}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className={labelCls}>{c.fMessage} <span className="text-onyx/40">({c.optional})</span></label>
                  <textarea rows={4} value={f.message} onChange={set("message")} className={`${field} resize-none`} data-testid="corp-message" />
                </div>

                <label className="flex cursor-pointer items-start gap-4" data-testid="corp-consent-label">
                  <input
                    type="checkbox"
                    checked={f.consent}
                    onChange={(e) => setF((p) => ({ ...p, consent: e.target.checked }))}
                    className="mt-1 h-5 w-5 shrink-0 accent-onyx"
                    data-testid="corp-consent"
                  />
                  <span className="font-sans text-sm leading-relaxed text-onyx/70">{c.consent} *</span>
                </label>

                <button type="submit" disabled={status === "sending"} className="label border border-onyx bg-onyx px-10 py-5 text-petal transition-colors duration-500 hover:bg-transparent hover:text-onyx disabled:opacity-50" data-testid="corp-submit">
                  {status === "sending" ? c.sending : c.submit}
                </button>
              </form>
            </>
          )}
        </div>
      </section>

      {/* 8 — FINAL CTA */}
      <section className="bg-onyx text-petal" data-testid="corporate-final">
        <div className="mx-auto flex max-w-[1600px] flex-col items-start gap-10 px-6 py-24 md:flex-row md:items-end md:justify-between md:px-12 md:py-32">
          <div className="max-w-2xl">
            <Reveal><h2 className="font-serif text-5xl leading-[0.98] md:text-7xl">{c.finalTitle}</h2></Reveal>
            <p className="mt-8 font-sans text-lg leading-relaxed text-petal/75">{c.finalBody}</p>
          </div>
          <button onClick={scrollToForm} className="label shrink-0 border border-petal bg-petal px-10 py-5 text-onyx transition-colors duration-500 hover:bg-transparent hover:text-petal" data-testid="corporate-final-cta">
            {c.finalCta}
          </button>
        </div>
      </section>
    </>
  );
}
